import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Button,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Language as LanguageIcon,
  Home as HomeIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import api from './api';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from './utils/languages';
import PaginationControls from './components/PaginationControls';

// Memoized table row component to prevent unnecessary re-renders
const FoodTableRow = React.memo(({ 
  food, 
  currentLanguage, 
  getFoodName, 
  getCategoryName, 
  handleFoodView, 
  handleFoodEdit 
}) => {
  const displayName = getFoodName(food);
  const categoryName = getCategoryName(food.category);
  
  // Memoized image URL calculation
  const getImageUrl = useCallback((food) => {
    // Check language-specific image first
    const langImage = food.languageSpec?.[currentLanguage]?.customImageUrl;
    if (langImage && (langImage.includes('http') || langImage.includes('data:'))) {
      return langImage;
    }
    
    // Check main image
    const mainImage = food.image;
    if (mainImage && typeof mainImage === 'string' && (mainImage.includes('http') || mainImage.includes('data:'))) {
      return mainImage;
    }
    
    // Return placeholder if no valid image URL found
    return 'https://via.placeholder.com/50x50/e0e0e0/757575?text=No+Image';
  }, [currentLanguage]);
  
  const imageUrl = getImageUrl(food);
  const hasImage = (food.image && (food.image.includes('http') || food.image.includes('data:'))) || 
                  (food.languageSpec?.[currentLanguage]?.customImageUrl && 
                   (food.languageSpec[currentLanguage].customImageUrl.includes('http') || 
                    food.languageSpec[currentLanguage].customImageUrl.includes('data:')));
  
  return (
    <TableRow key={food.id} hover sx={{ cursor: 'pointer' }}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={imageUrl}
            alt={displayName}
            sx={{ 
              width: 50, 
              height: 50,
              border: hasImage ? '2px solid #e0e0e0' : '2px dashed #bdbdbd',
              '& img': {
                objectFit: 'cover'
              }
            }}
            variant="rounded"
          />
          {!hasImage && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              No Image
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight="medium">
          {displayName}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={categoryName || 'N/A'}
          size="small"
          color="primary"
          variant="outlined"
        />
      </TableCell>
      <TableCell align="right">
        {food.calories ? Number(food.calories).toFixed(1) : '-'}
      </TableCell>
      <TableCell align="right">
        {food.protein ? Number(food.protein).toFixed(1) : '-'}
      </TableCell>
      <TableCell align="right">
        {food.fat ? Number(food.fat).toFixed(1) : '-'}
      </TableCell>
      <TableCell align="right">
        {food.carbs ? Number(food.carbs).toFixed(1) : '-'}
      </TableCell>
      <TableCell align="right">
        {food.glycemicIndex ? Number(food.glycemicIndex).toFixed(0) : '-'}
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2">
          {food.unitType || '-'}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2">
          {food.unitValue || '-'}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleFoodView(food.id)}
              color="primary"
              sx={{ 
                border: '1px solid',
                borderColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.light', color: 'white' }
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Edit Food">
            <IconButton
              size="small"
              onClick={() => handleFoodEdit(food.id)}
              color="secondary"
              sx={{ 
                border: '1px solid',
                borderColor: 'secondary.main',
                '&:hover': { backgroundColor: 'secondary.light', color: 'white' }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});

export default function SearchFoodManager() {
  const navigate = useNavigate();
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  
  // Default languages
  const defaultLanguages = AVAILABLE_LANGUAGES;
  
  // Ref to track current search query to avoid stale closures
  const isInitialMount = useRef(true);
  const lastSearchQuery = useRef(searchQuery);
  
  // Load data on component mount and when filters change
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchFoods = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page, // API expects 0-based pagination
        number: rowsPerPage,
        language: currentLanguage
      };
      
      if (categoryFilter && categoryFilter !== 'all') {
        params.category = categoryFilter;
      }
      
      // Only add query parameter if search query has 3 or more characters
      // Otherwise, fetch all foods (no query parameter)
      if (searchQuery.trim() && searchQuery.trim().length >= 3) {
        params.query = searchQuery.trim();
      }
      
      const response = await api.get('/foods/search-for-admin-panel', { params });
      
      if (response.data.success) {
        setFoods(response.data.data.foods);
        // Check for different possible field names for total count
        const totalCountFromApi = response.data.data.totalResults || 
                                response.data.data.totalCount || 
                                response.data.data.total || 
                                response.data.data.foods.length;
        setTotalCount(totalCountFromApi);
      }
    } catch (error) {
      console.error('Failed to fetch foods:', error);
      setFoods([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, currentLanguage, categoryFilter, searchQuery]);
  
  // Effect for non-search changes (pagination, language, category, etc.)
  useEffect(() => {
    // Skip on initial mount to prevent duplicate calls
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchFoods();
      return;
    }
    
    // Skip if this effect was triggered by searchQuery change
    if (searchQuery !== lastSearchQuery.current) {
      return;
    }
    
    fetchFoods();
  }, [fetchFoods, page, rowsPerPage, categoryFilter, currentLanguage, searchQuery]);

  // Separate effect for debounced search
  useEffect(() => {
    // Skip on initial mount to prevent duplicate calls
    if (isInitialMount.current) {
      return;
    }
    
    lastSearchQuery.current = searchQuery;
    
    const timeoutId = setTimeout(() => {
      setPage(0); // Reset to first page when searching
      fetchFoods();
    }, 1500); // 1500ms debounce delay (same as FoodManager)
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, fetchFoods]);
  
  const fetchCategories = async () => {
    try {
      const response = await api.get('/category/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };
  

  
  // Handle pagination
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);
  
  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);
  
  // Handle search
  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);
  
  // Handle language change
  const handleLanguageChange = useCallback((event) => {
    setCurrentLanguage(event.target.value);
    setPage(0);
  }, []);
  
  // Handle category filter change
  const handleCategoryFilterChange = useCallback((event) => {
    setCategoryFilter(event.target.value);
    setPage(0);
  }, []);
  
  // Handle food selection for viewing details
  const handleFoodView = useCallback((foodId) => {
    navigate(`/food-detail/${foodId}`);
  }, [navigate]);
  
  // Handle food editing
  const handleFoodEdit = useCallback((foodId) => {
    // Navigate to FoodManager with specific food ID for editing
    navigate(`/food-manager?edit=${foodId}`);
  }, [navigate]);
  
  // Get food name based on current language
  const getFoodName = useCallback((food) => {
    if (food.languageSpec && food.languageSpec[currentLanguage] && food.languageSpec[currentLanguage].name) {
      return food.languageSpec[currentLanguage].name;
    }
    return food.name;
  }, [currentLanguage]);
  
  // Get category name based on current language
  const getCategoryName = useCallback((categoryName) => {
    const category = categories.find(cat => cat.name === categoryName);
    if (category && category.languageSpec && category.languageSpec[currentLanguage]) {
      return category.languageSpec[currentLanguage].name;
    }
    return categoryName;
  }, [categories, currentLanguage]);
  
  return (
    <Box sx={{ width: '100%', maxWidth: '100vw', px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          component="button"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Typography color="text.primary">Food Search</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Food Database</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => navigate('/food-manager')}
        >
          Manage Foods
        </Button>
      </Box>
      
      {/* Search and Filter Controls */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon color="action" />
            <Typography variant="h6">Search & Filter</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <LanguageIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              Language:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={currentLanguage}
                onChange={handleLanguageChange}
                displayEmpty
              >
                {defaultLanguages.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {lang.charAt(0).toUpperCase() + lang.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search foods (min. 3 characters)..."
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 250, flexGrow: 1 }}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
            }}
            helperText={searchQuery.length > 0 && searchQuery.length < 3 ? "Enter at least 3 characters to search" : "Leave empty to show all foods"}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={handleCategoryFilterChange}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map(category => (
                <MenuItem key={category.id} value={category.name}>
                  {getCategoryName(category.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>
      
      {/* Results Summary */}
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {loading ? 'Loading...' : `${totalCount} foods found`}
          </Typography>
          
          {searchQuery && (
            <Chip 
              label={`Search: "${searchQuery}"`} 
              onDelete={() => setSearchQuery('')}
              color="primary" 
              variant="outlined"
            />
          )}
          
          {categoryFilter !== 'all' && (
            <Chip 
              label={`Category: ${getCategoryName(categoryFilter)}`} 
              onDelete={() => setCategoryFilter('all')}
              color="secondary" 
              variant="outlined"
            />
          )}
        </Box>
      </Paper>
      
      {/* Food Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Calories</TableCell>
                <TableCell align="right">Protein (g)</TableCell>
                <TableCell align="right">Fat (g)</TableCell>
                <TableCell align="right">Carbs (g)</TableCell>
                <TableCell align="right">Glycemic Index</TableCell>
                <TableCell align="center">Unit Type</TableCell>
                <TableCell align="center">Unit Value</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {foods.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchQuery || categoryFilter !== 'all' 
                        ? 'No foods found matching your criteria' 
                        : 'No foods available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                foods.map((food) => (
                  <FoodTableRow
                    key={food.id}
                    food={food}
                    currentLanguage={currentLanguage}
                    getFoodName={getFoodName}
                    getCategoryName={getCategoryName}
                    handleFoodView={handleFoodView}
                    handleFoodEdit={handleFoodEdit}
                  />
                ))
              )}
            </TableBody>
          </Table>
          
          <PaginationControls
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[50, 100, 200, 500]}
            loading={loading}
          />
        </TableContainer>
      </Paper>
    </Box>
  );
}